import type { IntlShape } from 'react-intl'

import messages from 'Translations/index'

import {
  ANONYMISED_FALLBACK_LABEL,
  ANONYMISED_TRANSLATION_KEY,
  ENCRYPTED_MARKER,
  LEGACY_ANONYMISED_PREFIX,
} from './anonymisedContact.constants'
import type {
  AnonymisableAddress,
  AnonymisableContact,
} from './anonymisedContact.types'

/**
 * Reports whether a single field still holds what an anonymisation run left behind. Both markers
 * are matched by prefix: the current one appends the employee id on the external reference, the
 * legacy one appends the ciphertext.
 */
export const isAnonymisedValue = (value?: string | null): boolean =>
  !!value &&
  (value.startsWith(ENCRYPTED_MARKER) ||
    value.startsWith(LEGACY_ANONYMISED_PREFIX))

/**
 * An address is anonymised as soon as any of its fields is, which is what recognises a contact
 * that held nothing but an address.
 */
export const isAnonymisedAddress = (
  address?: AnonymisableAddress | null,
): boolean =>
  !!address &&
  (isAnonymisedValue(address.name) ||
    isAnonymisedValue(address.nameAddition) ||
    isAnonymisedValue(address.street) ||
    isAnonymisedValue(address.streetAddition) ||
    isAnonymisedValue(address.zipcode) ||
    isAnonymisedValue(address.city) ||
    isAnonymisedValue(address.country))

/**
 * A contact is anonymised as soon as any of its PII fields is. The encryption runs field by field
 * and skips the empty ones, so a contact that carried nothing but a phone number is recognised by
 * that phone number alone, and a contact whose name an earlier run replaced stays masked even
 * where a later field was skipped.
 */
export const isAnonymisedContact = (
  contact?: AnonymisableContact | null,
): boolean =>
  !!contact &&
  (isAnonymisedValue(contact.firstName) ||
    isAnonymisedValue(contact.lastName) ||
    isAnonymisedValue(contact.email) ||
    isAnonymisedValue(contact.phoneNumber) ||
    isAnonymisedAddress(contact.address))

export const getAnonymisedLabel = (intl?: IntlShape): string =>
  intl?.formatMessage({ id: ANONYMISED_TRANSLATION_KEY }) ??
  ANONYMISED_FALLBACK_LABEL

/**
 * The same label for the places that hold the language but no intl - a store outside the provider
 * tree. It reads the dictionary the provider is configured from, so both stay in step.
 */
export const getAnonymisedLabelForLanguage = (language?: string): string =>
  (language ? messages[language] : undefined)?.[ANONYMISED_TRANSLATION_KEY] ??
  ANONYMISED_FALLBACK_LABEL

/**
 * Returns the label in place of an anonymised value and the value itself otherwise, for a field
 * that reaches the UI without the contact it belongs to.
 */
export function maskAnonymisedValue(
  value: string | null | undefined,
  intl?: IntlShape,
): string | null | undefined {
  return isAnonymisedValue(value) ? getAnonymisedLabel(intl) : value
}

/**
 * Masks a field by the state of the whole contact rather than by the field alone: the earlier
 * anonymisation runs only rewrote the names and the email, so a phone number or an employee id of
 * such a contact still reads as the real thing while the identity it belongs to is gone.
 */
export function maskAnonymisedContactValue(
  value: string | null | undefined,
  contact?: AnonymisableContact | null,
  intl?: IntlShape,
): string | null | undefined {
  return isAnonymisedContact(contact) || isAnonymisedValue(value)
    ? getAnonymisedLabel(intl)
    : value
}
